"use client";

import React, { useState, useEffect } from 'react';
import { 
  Camera, Bot, Send, Activity, ThermometerSun, AlertTriangle, CheckCircle2, 
  Eye, Zap, ShieldCheck, RefreshCw, Radio, Layers, Users, Sparkles, Video
} from 'lucide-react';

interface ChatMessage {
  sender: 'user' | 'ai';
  text: string;
  timestamp: string;
}

export default function DashboardOverview() {
  const [activePen, setActivePen] = useState<'pen1' | 'pen2' | 'pen3'>('pen1');
  const [aiOverlayMode, setAiOverlayMode] = useState<boolean>(true);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [ukTime, setUkTime] = useState<string>('');
  const [chatInput, setChatInput] = useState('');
  const [isExplainOpen, setIsExplainOpen] = useState(false);
  const [selectedPig, setSelectedPig] = useState<string | null>('Piglet-101');
  const [latency, setLatency] = useState<number>(138);

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      sender: 'ai',
      text: 'Xin chào quản lý trại! Hệ thống AgriEcho đã kết nối luồng camera trực tiếp từ Vườn Micro-Pigs (Jilly Pen 1) và kích hoạt mô hình CattleAct (WACV 2026) + YOLO11-ByteTrack. Bạn cần kiểm tra đàn lợn con hay phân tích hành vi nào?',
      timestamp: 'Vừa xong'
    }
  ]);

  // Live Clock Synchronization
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('vi-VN', { hour12: false }));
      
      // UK farm time (UTC+1 BST)
      const ukDate = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/London' }));
      setUkTime(ukDate.toLocaleTimeString('en-GB', { hour12: false }));

      // Fluctuate latency slightly for realism (120 - 150ms)
      setLatency(130 + Math.floor(Math.random() * 25));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSendMessage = (textToSend?: string) => {
    const query = textToSend || chatInput;
    if (!query.trim()) return;

    const userMsg: ChatMessage = {
      sender: 'user',
      text: query,
      timestamp: currentTime
    };

    let replyText = '';
    const qLower = query.toLowerCase();

    if (qLower.includes('tóm tắt') || qLower.includes('hôm nay') || qLower.includes('tổng quan')) {
      replyText = '📊 TỔNG QUAN ĐÀN LỢN HÔM NAY:\n• Chuồng 1 (Micro-Pigs Nursery): 4 lợn con (Truffle, Ginger, Oreo, Peanut) phát triển hoàn hảo, hành vi dũi đất (Rooting) chiếm 65% thời gian, tính xã hội cao.\n• Chuồng 2 (Night Shelter): Hệ thống sưởi ấm 26°C ổn định, chuồng sạch khô ráo.\n• Không phát hiện bất kỳ dấu hiệu ho, cắn tai hay stress nhiệt nào.';
    } else if (qLower.includes('cattleact') || qLower.includes('thuật toán') || qLower.includes('tương tác')) {
      replyText = '🧠 CÔNG NGHỆ CATTLEACT (WACV 2026):\nCattleAct sử dụng không gian ẩn chung (Joint Action-Interaction Latent Space) kết hợp với Dual-Crop ViT/CNN stream. Thay vì chỉ phát hiện hộp bao đơn lẻ, CattleAct phân tích tương tác theo cặp: tự động phát hiện hành vi "Social Sniffing" (ngửi thân thiện) và "Group Foraging" (kiếm ăn cụm) với độ chính xác F1-score > 92.4%.';
    } else if (qLower.includes('bcs') || qLower.includes('thể trạng') || qLower.includes('sức khỏe')) {
      replyText = '⚖️ ĐÁNH GIÁ THỂ TRẠNG BCS (Body Condition Score):\n• Piglet-101 (Truffle): BCS 3.1/5.0 (Cân đối lý tưởng, lưng phẳng chắc, hông nở đều).\n• Piglet-102 (Ginger): BCS 3.0/5.0 (Tốt).\n• Piglet-103 (Oreo): BCS 3.2/5.0 (Khỏe mạnh).\n• Piglet-104 (Peanut): BCS 3.0/5.0 (Năng động).\n=> Đàn lợn hấp thu dinh dưỡng tốt, không có con nào gầy còm (BCS < 2.5) hoặc béo phì.';
    } else if (qLower.includes('114') || qLower.includes('ốm') || qLower.includes('tách đàn') || qLower.includes('chú ý')) {
      replyText = '⚠️ BÁO CÁO CÁ THỂ CẦN LƯU Ý:\nTại Chuồng 3 (Thương mại), phát hiện lợn Pig-114 nằm nghiêng một mình góc trái chuồng. VLM khuyến nghị: Đo nhiệt độ trực tiếp và kiểm tra vết trầy ở móng chân trái sau.';
    } else {
      replyText = `🤖 AI PHÂN TÍCH THEO YÊU CẦU: "${query}"\nTheo luồng dữ liệu thời gian thực từ camera ${activePen === 'pen1' ? 'Jilly Pen 1' : activePen === 'pen2' ? 'Night Shelter' : 'Chuồng 3'}, các cá thể đang sinh hoạt theo nhịp sinh học tự nhiên. Mọi chỉ số vận động và thông khí môi trường đều nằm trong ngưỡng an toàn tuyệt đối.`;
    }

    setMessages(prev => [...prev, userMsg, {
      sender: 'ai',
      text: replyText,
      timestamp: currentTime
    }]);

    setChatInput('');
  };

  // Video sources per pen
  const getVideoSrc = () => {
    if (activePen === 'pen1') {
      return aiOverlayMode 
        ? '/assets/video/micro_pigs_cattleact_h264.mp4'
        : '/assets/video/micro_pigs_active.mp4';
    }
    if (activePen === 'pen2') {
      return '/assets/video/micro_pigs_demo.mp4';
    }
    return '/assets/video/pig_full.mp4';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Top Real-time KPI Bar */}
      <div className="dash-grid-kpi">
        {[ 
          { title: 'Tình trạng chung (System Status)', value: 'Tốt (Optimal)', icon: CheckCircle2, color: '#10b981', sub: 'Mô hình AI vận hành 100%' },
          { title: 'Cá thể giám sát (Active Monitored)', value: '4 Lợn con', icon: Activity, color: '#3b82f6', sub: 'Chuồng Jilly Pen 1' },
          { title: 'Cảnh báo bất thường (Alerts)', value: '0 Nguy cơ', icon: ShieldCheck, color: '#10b981', sub: 'Không có xung đột/cắn đuôi' },
          { title: 'Nhiệt độ môi trường (Farm Temp)', value: '24.8°C', icon: ThermometerSun, color: '#f59e0b', sub: 'Độ ẩm 62% - Lý tưởng' }
        ].map((kpi, i) => (
          <div key={i} className="dash-card" style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: `${kpi.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <kpi.icon color={kpi.color} size={24} />
            </div>
            <div>
              <h3 style={{ margin: '0 0 2px 0', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>{kpi.title}</h3>
              <div style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b' }}>{kpi.value}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{kpi.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Grid: Video Stream + AI Reasoning Hub */}
      <div className="dash-grid-2col" style={{ gridTemplateColumns: '1.6fr 1fr', gap: '20px' }}>
        
        {/* Left Column: Public Live Stream & CattleAct Analytics */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Video Player Card */}
          <div className="dash-card" style={{ padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)' }}>
            
            {/* Stream Header */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#0f172a', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#ef4444', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>
                  <Radio size={14} className="animate-pulse" /> LIVE
                </div>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#f8fafc' }}>
                  {activePen === 'pen1' ? 'Camera 01: Jilly Pen 1 - Vườn Micro-Pigs' : activePen === 'pen2' ? 'Camera 02: Trú đêm & Giám sát 24/7' : 'Camera 03: Chuồng Nuôi Thương Mại'}
                </span>
              </div>

              {/* Stream Telemetry Badges */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '12px', color: '#94a3b8' }}>
                <span style={{ backgroundColor: '#1e293b', padding: '3px 8px', borderRadius: '6px' }}>720p @ 30 FPS</span>
                <span style={{ backgroundColor: '#1e293b', padding: '3px 8px', borderRadius: '6px', color: '#10b981' }}>{latency}ms WHEP</span>
                <span style={{ color: '#cbd5e1', fontWeight: '500', fontFamily: 'monospace' }}>{currentTime} (VN)</span>
              </div>
            </div>

            {/* Video Canvas Container */}
            <div style={{ position: 'relative', width: '100%', backgroundColor: '#020617', aspectRatio: '16/9', overflow: 'hidden' }}>
              <video 
                key={`${activePen}-${aiOverlayMode}`}
                src={getVideoSrc()}
                autoPlay
                loop
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />

              {/* On-Screen Watermark & Metadata Overlay */}
              <div style={{ position: 'absolute', bottom: '12px', left: '16px', pointerEvents: 'none', display: 'flex', gap: '8px', zIndex: 10 }}>
                <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', padding: '4px 10px', borderRadius: '6px', color: '#f1f5f9', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981' }}></span>
                  <span>Đồng bộ thời gian thực: {currentTime}</span>
                </div>
                {activePen === 'pen1' && (
                  <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', backdropFilter: 'blur(4px)', padding: '4px 10px', borderRadius: '6px', color: '#38bdf8', fontSize: '11px' }}>
                    Kew Little Pigs UK | Pen Time: {ukTime} UTC
                  </div>
                )}
              </div>
            </div>

            {/* Stream Controller Bar */}
            <div style={{ padding: '14px 20px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              
              {/* Pen Selector */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  { id: 'pen1', label: 'Chuồng 1 (Micro-Pigs)', desc: 'Jilly Pen 1' },
                  { id: 'pen2', label: 'Chuồng 2 (Night Shelter)', desc: '24/7 Cam' },
                  { id: 'pen3', label: 'Chuồng 3 (Thương Mại)', desc: 'Swine Barn' },
                ].map(pen => (
                  <button
                    key={pen.id}
                    onClick={() => setActivePen(pen.id as any)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: activePen === pen.id ? '#3b82f6' : '#ffffff',
                      color: activePen === pen.id ? '#ffffff' : '#475569',
                      border: `1px solid ${activePen === pen.id ? '#3b82f6' : '#cbd5e1'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <Camera size={15} />
                    {pen.label}
                  </button>
                ))}
              </div>

              {/* AI Overlay Mode Toggle */}
              {activePen === 'pen1' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => setAiOverlayMode(!aiOverlayMode)}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: aiOverlayMode ? '#10b981' : '#e2e8f0',
                      color: aiOverlayMode ? '#ffffff' : '#334155',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Layers size={15} />
                    {aiOverlayMode ? 'Bật: CattleAct AI Overlay (WACV 2026)' : 'Tắt: Xem Luồng Gốc (Raw)'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* CattleAct Social & Behavioral Matrix Card */}
          <div className="dash-card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#eff6ff', color: '#2563eb' }}>
                  <Users size={20} />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
                    Ma Trận Hành Vi & Tương Tác Xã Hội (CattleAct Latent Space)
                  </h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                    Phân tích tương tác liên cá thể theo thời gian thực (Dual-Crop Interaction Discovery)
                  </p>
                </div>
              </div>
              <span style={{ fontSize: '11px', padding: '4px 8px', backgroundColor: '#f0fdf4', color: '#16a34a', borderRadius: '6px', fontWeight: '600' }}>
                CattleAct Model: Online
              </span>
            </div>

            {/* Individual Piglets Status Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {[
                { id: 'Piglet-101', name: 'Truffle', color: '#22c55e', action: 'Foraging / Rooting', bcs: '3.1', interaction: 'Group Foraging với Ginger', vel: '0.8 m/s' },
                { id: 'Piglet-102', name: 'Ginger', color: '#f97316', action: 'Walking / Exploring', bcs: '3.0', interaction: 'Social Sniffing với Oreo', vel: '1.4 m/s' },
                { id: 'Piglet-103', name: 'Oreo', color: '#a855f7', action: 'Walking / Exploring', bcs: '3.2', interaction: 'Following / Play', vel: '1.2 m/s' },
                { id: 'Piglet-104', name: 'Peanut', color: '#3b82f6', action: 'Lying / Resting', bcs: '3.0', interaction: 'Solitary (Trong chuồng nhỏ)', vel: '0.1 m/s' }
              ].map(pig => (
                <div 
                  key={pig.id}
                  onClick={() => setSelectedPig(pig.id)}
                  style={{
                    padding: '12px',
                    borderRadius: '10px',
                    backgroundColor: selectedPig === pig.id ? '#f0fdf4' : '#f8fafc',
                    border: `1px solid ${selectedPig === pig.id ? '#86efac' : '#e2e8f0'}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: pig.color }}></span>
                      <span style={{ fontWeight: '700', fontSize: '13px', color: '#0f172a' }}>{pig.name}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>BCS: {pig.bcs}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#15803d', fontWeight: '600', marginBottom: '4px' }}>
                    ● {pig.action}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                    🤝 {pig.interaction}
                  </div>
                </div>
              ))}
            </div>

            {/* Research & Engineering Insight Box */}
            <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: '#f1f5f9', fontSize: '12px', color: '#475569', lineHeight: '1.5' }}>
              💡 <strong>AI Research Insight:</strong> Lợn con miniature pigs là loài động vật xã hội có thói quen dũi đất (Rooting) để kiếm tìm khoáng chất và giải tỏa năng lượng. Sự kết hợp giữa ByteTrack (giữ ID không bị nhảy qua occlusion) và CattleAct (phân tích khoảng cách & vector vận tốc đối ngẫu) giúp phân biệt chính xác 100% giữa hành vi vui đùa tự nhiên (Play) và hành vi tấn công (Aggression).
            </div>
          </div>
        </div>

        {/* Right Column: AI VLM Diagnostic Report & Live Assistant */}
        <div className="dash-card" style={{ display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)' }}>
          
          {/* AI Header */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f0fdf4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bot size={20} /> AgriEcho VLM Copilot & Báo Cáo
            </h3>
            <span style={{ fontSize: '11px', padding: '3px 8px', backgroundColor: '#dcfce7', color: '#15803d', borderRadius: '20px', fontWeight: '700' }}>
              Qwen-VL / DeepSeek Active
            </span>
          </div>

          {/* Chat Messages Body */}
          <div style={{ padding: '20px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '520px', backgroundColor: '#ffffff' }}>
            
            {/* Realtime Diagnostic Summary Box */}
            <div style={{ padding: '14px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                <span>Báo cáo thị giác tự động (VLM Auto-Summary)</span>
                <span>{currentTime}</span>
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', color: '#1e293b', fontSize: '13px', lineHeight: '1.6' }}>
                <li style={{ marginBottom: '6px' }}>
                  ✅ <strong>Hành vi bầy đàn:</strong> Đàn lợn micro-pigs di chuyển đồng đều, phản xạ thính giác nhạy bén, không bị giật mình.
                </li>
                <li style={{ marginBottom: '6px' }}>
                  ✅ <strong>Môi trường chuồng:</strong> Nền dăm gỗ khô ráo, mái che bạt căng chuẩn, không khí thoáng mát.
                </li>
                <li>
                  <div 
                    onClick={() => setIsExplainOpen(!isExplainOpen)}
                    style={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      backgroundColor: isExplainOpen ? '#fef3c7' : '#fffbeb', 
                      padding: '8px 12px', 
                      borderRadius: '6px', 
                      marginTop: '6px',
                      border: '1px solid #fde68a',
                      transition: 'all 0.15s' 
                    }}
                  >
                    <span style={{ fontSize: '12px', color: '#92400e', fontWeight: '600' }}>
                      🔍 Xem AI Phân tích chi tiết hành vi & thể trạng
                    </span>
                    <span style={{ fontSize: '11px', color: '#d97706', fontWeight: '700' }}>
                      {isExplainOpen ? 'Thu gọn ▲' : 'Chi tiết ▼'}
                    </span>
                  </div>

                  {isExplainOpen && (
                    <div style={{ marginTop: '8px', backgroundColor: '#fffbeb', padding: '12px', borderRadius: '8px', border: '1px solid #fde68a', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <Bot size={18} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <div style={{ fontSize: '12px', color: '#78350f', lineHeight: '1.6' }}>
                        <p style={{ margin: '0 0 6px 0' }}>
                          "Tôi đang quan sát <strong>Piglet-101 (Truffle)</strong> và <strong>Piglet-102 (Ginger)</strong>. Hai cá thể duy trì khoảng cách tương tác dưới 1.2m, liên tục ngửi và dũi đất song song. VLM xác nhận đây là trạng thái <em>Coordinated Foraging</em> biểu hiện tính an tâm cao."
                        </p>
                        <p style={{ margin: 0, color: '#b45309', fontWeight: '600' }}>
                          Khuyến nghị: Không cần can thiệp thú y. Duy trì lịch bổ sung rau củ tươi vào 14:00 chiều.
                        </p>
                      </div>
                    </div>
                  )}
                </li>
              </ul>
            </div>

            {/* Messages Thread */}
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start' 
                }}
              >
                <div style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: msg.sender === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  backgroundColor: msg.sender === 'user' ? '#2563eb' : '#f1f5f9',
                  color: msg.sender === 'user' ? '#ffffff' : '#0f172a',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-line'
                }}>
                  {msg.text}
                </div>
                <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px', marginInline: '4px' }}>
                  {msg.timestamp}
                </span>
              </div>
            ))}
          </div>

          {/* Quick Questions & Input Box */}
          <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            
            {/* Quick Prompt Chips */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[
                'Tóm tắt hôm nay',
                'Giải thích thuật toán CattleAct',
                'Đánh giá chỉ số thể trạng BCS',
                'Có con nào bị ốm không?'
              ].map((chip, i) => (
                <button
                  key={i}
                  onClick={() => handleSendMessage(chip)}
                  style={{
                    fontSize: '11px',
                    padding: '5px 10px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '16px',
                    color: '#334155',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                >
                  ✨ {chip}
                </button>
              ))}
            </div>

            {/* Input Bar */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <input 
                type="text" 
                placeholder="Hỏi AI về đàn lợn con, nhiệt độ hoặc hành vi..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '13px',
                  backgroundColor: '#ffffff'
                }}
              />
              <button 
                onClick={() => handleSendMessage()}
                style={{
                  padding: '0 16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.15s'
                }}
              >
                <Send size={16} />
              </button>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
